// Forældreansvarsloven-kursus · afspiller
// Ren JavaScript uden afhængigheder. Monteres i et element i portalen:
//
//   import { mountCourse } from './player.js';
//   import COURSE from './content.js';
//   mountCourse(document.getElementById('kursus'), { course: COURSE, apiBase: '/api/training' });
//
// Se README.md og API.md for hændelser og endpoints.

const esc = s => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2));

export function mountCourse(root, opts = {}) {
  const course = opts.course;
  if (!root || !course) throw new Error("mountCourse: root og opts.course er påkrævet");
  const S = course.scenes, CH = course.chapters;
  const idxById = new Map(S.map((s, i) => [s.id, i]));
  const allQs = S.filter(s => s.quiz).flatMap(s => s.qs.map(q => ({ ...q, sceneId: s.id, c: s.c })));
  const contentIds = S.filter(s => !s.quiz && !s.end).map(s => s.id);
  const apiBase = opts.apiBase ? opts.apiBase.replace(/\/$/, "") + "/" + encodeURIComponent(course.id) : null;
  const fetchInit = opts.fetchInit || {};
  const pendKey = "fak-pending-" + course.id + "-" + (opts.user?.id ?? "anon");

  const state = { seen: new Set(), answers: {}, lastSceneId: null, completedAt: null };
  let idx = 0, playing = false, t = 0, last = performance.now(), speed = 1, raf = 0, destroyed = false;

  /* ---------- sync ---------- */
  let queue = [];
  try { queue = JSON.parse(localStorage.getItem(pendKey) || "[]"); } catch (e) {}
  let flushing = false, syncState = "ok";
  const persistQueue = () => { try { localStorage.setItem(pendKey, JSON.stringify(queue)); } catch (e) {} };

  function emit(type, data = {}) {
    const evt = { type, courseId: course.id, courseVersion: course.version, clientEventId: uid(), at: new Date().toISOString(), ...data };
    queue.push(evt); persistQueue(); flush();
    try { opts.onLocalEvent?.(evt); } catch (e) {}
  }
  async function send(evts) {
    if (opts.onEvent) { for (const e of evts) await opts.onEvent(e); return; }
    if (!apiBase) return; // ingen backend: kører kun lokalt
    const res = await fetch(apiBase + "/events", {
      method: "POST", credentials: "include", ...fetchInit,
      headers: { "Content-Type": "application/json", ...(fetchInit.headers || {}) },
      body: JSON.stringify({ events: evts })
    });
    if (!res.ok) throw new Error("HTTP " + res.status);
  }
  async function flush() {
    if (flushing || !queue.length) return;
    flushing = true;
    const batch = queue.slice(0, 50);
    try {
      await send(batch);
      queue = queue.slice(batch.length); persistQueue(); setSync("ok");
      flushing = false; if (queue.length) flush();
    } catch (e) {
      flushing = false; setSync("err");
      setTimeout(() => !destroyed && flush(), 8000);
    }
  }
  function setSync(s) {
    syncState = s;
    const el = root.querySelector(".fak-sync");
    if (el) { el.textContent = s === "err" ? "Kunne ikke gemme. Prøver igen…" : (opts.apiBase || opts.onEvent ? "Din fremgang gemmes" : "Demo: fremgang gemmes ikke"); el.classList.toggle("err", s === "err"); }
  }

  function applyProgress(p) {
    if (!p) return;
    (p.seen || []).forEach(id => idxById.has(id) && state.seen.add(id));
    state.attemptCount = {};
    for (const [qid, a] of Object.entries(p.answers || {})) { state.answers[qid] = a; state.attemptCount[qid] = a.attempt || 1; }
    for (const [qid, n] of Object.entries(p.attempts || {})) state.attemptCount[qid] = n;
    state.lastSceneId = p.lastSceneId || null;
    state.completedAt = p.completedAt || null;
  }

  /* ---------- visuals ---------- */
  function vPpl(v) {
    const N = {}; v.n.forEach(n => N[n[0]] = { l: n[1], x: n[2], y: n[3], r: n[4] });
    const used = new Set(), pairs = {}; let out = "";
    v.l.forEach((l, i) => {
      const a = N[l[0]], b = N[l[1]], st = l[2]; used.add(st);
      const key = [l[0], l[1]].sort().join(); const k = (pairs[key] = (pairs[key] || 0) + 1) - 1;
      const dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy) || 1, nx = -dy / len, ny = dx / len;
      const off = st === "fm" ? 0 : (k % 2 ? -1 : 1) * 14 * Math.ceil(k);
      const ra = a.r === "c" ? 22 : 26, rb = b.r === "c" ? 22 : 26;
      const x1 = a.x + dx / len * ra + nx * off, y1 = a.y + dy / len * ra + ny * off, x2 = b.x - dx / len * rb + nx * off, y2 = b.y - dy / len * rb + ny * off;
      const SS = { fm: 'stroke="var(--fak-teal)" stroke-width="5" stroke-linecap="round"', bo: 'stroke="var(--fak-bluel)" stroke-width="2.5"', sv: 'stroke="var(--fak-mist)" stroke-width="2" stroke-dasharray="6 5"', x: 'stroke="var(--fak-bad)" stroke-width="2" stroke-dasharray="4 5"', q: 'stroke="var(--fak-teal)" stroke-width="2" stroke-dasharray="2 5" stroke-linecap="round"', thin: 'stroke="var(--fak-mist2)" stroke-width="1.5"' }[st];
      out += `<line class="fak-fade" style="--d:${.35 + i * .25}s" x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" fill="none" ${SS}/>`;
      const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
      if (st === "x") out += `<g class="fak-fade" style="--d:${.5 + i * .25}s" stroke="var(--fak-bad)" stroke-width="2.5"><line x1="${mx - 6}" y1="${my - 6}" x2="${mx + 6}" y2="${my + 6}"/><line x1="${mx + 6}" y1="${my - 6}" x2="${mx - 6}" y2="${my + 6}"/></g>`;
      if (l[3]) { const s = off >= 0 ? 16 : -16; out += `<text class="fak-fade" style="--d:${.6 + i * .25}s" x="${mx + nx * s}" y="${my + ny * s + 4}" text-anchor="middle" font-size="12" fill="var(--fak-mist2)" font-family="Instrument Sans,sans-serif">${esc(l[3])}</text>`; }
    });
    let nodes = ""; Object.values(N).forEach((n, i) => {
      const r = n.r === "c" ? 22 : 26;
      const fill = n.r === "p" ? "var(--fak-blue)" : n.r === "c" ? "var(--fak-teal)" : "var(--fak-stage2)";
      const stroke = n.r === "o" ? 'stroke="var(--fak-mist2)" stroke-width="1.5"' : 'stroke="#000" stroke-width=".8"';
      nodes += `<g class="fak-a" style="--d:${i * .15}s"><circle cx="${n.x}" cy="${n.y}" r="${r}" fill="${fill}" ${stroke}/><text x="${n.x}" y="${n.y + r + 17}" text-anchor="middle" font-size="13" fill="var(--fak-mist)" font-family="Instrument Sans,sans-serif">${esc(n.l)}</text></g>`;
    });
    const L = { fm: "forældremyndighed", bo: "bopæl", sv: "samvær", x: "ingen forbindelse", q: "anmodning eller uafklaret" };
    const leg = [...used].filter(u => L[u]).map(u => `<span><i class="${u === "fm" ? "" : u}"></i>${L[u]}</span>`).join("");
    return `<svg viewBox="0 0 400 245" role="img" aria-label="Diagram over forældre og barn">${out}${nodes}</svg><div class="fak-legend">${leg}</div>`;
  }
  function vis(v) {
    if (!v) return "";
    switch (v.k) {
      case "ppl": return vPpl(v);
      case "big": return `<div class="fak-big"><div class="n fak-a">${esc(v.n)}${v.u ? `<small>${esc(v.u)}</small>` : ""}</div><div class="cap fak-a" style="--d:.3s">${esc(v.cap)}</div></div>`;
      case "chk": return `<ul class="fak-chk">${v.h ? `<li class="h fak-a">${esc(v.h)}</li>` : ""}${v.i.map((x, i) => `<li class="it fak-a" style="--d:${.15 + i * .22}s"><span class="fak-mk ${x[0]}" aria-hidden="true">${x[0] === "y" ? "✓" : x[0] === "n" ? "✕" : "§"}</span><span>${esc(x[1])}</span></li>`).join("")}</ul>`;
      case "steps": return `<div>${v.i.map((x, i) => `<div class="fak-step fak-a" style="--d:${.15 + i * .3}s"><span class="dot">${i + 1}</span><span class="tx">${esc(x)}</span></div>`).join("")}</div>`;
      case "split": return `<div class="fak-split">${[["a", v.a], ["b", v.b]].map((c, j) => `<div class="col ${c[0]} fak-a" style="--d:${.15 + j * .35}s"><h4>${esc(c[1][0])}</h4><ul>${c[1][1].map(x => `<li>${esc(x)}</li>`).join("")}</ul></div>`).join("")}</div>`;
      case "tl": return `<div class="fak-tl">${v.i.map((x, i) => `<div class="pt fak-a" style="--d:${.15 + i * .3}s"><div class="l">${esc(x[0])}</div><div class="s">${esc(x[1])}</div></div>`).join("")}</div>`;
      case "word": { const pos = [[8, 12], [62, 6], [4, 78], [60, 84]]; return `<div class="fak-word"><div class="core fak-a">${esc(v.w)}</div>${v.i.map((x, i) => `<span class="orb fak-a" style="--d:${.3 + i * .2}s;left:${pos[i % 4][0]}%;top:${pos[i % 4][1]}%">${esc(x)}</span>`).join("")}</div>`; }
    }
    return "";
  }

  /* ---------- skeleton ---------- */
  root.classList.add("fak");
  root.dataset.theme = opts.theme || "light";
  root.innerHTML = `
  <div class="fak-intro">
    <h2 class="fak-h1">Forældreansvarsloven <i>§ for §</i></h2>
    <div class="fak-meta"><span class="fak-status">Gældende udgave</span><br><strong>${esc(course.source.label)}.</strong> ${opts.user?.name ? `Du er logget ind som ${esc(opts.user.name)}. ` : ""}<span class="fak-sync"></span></div>
  </div>
  <div class="fak-grid">
    <div>
      <section class="fak-player" aria-label="Afspiller">
        <div class="fak-stage" aria-live="polite"></div>
        <div class="fak-ctrl">
          <div class="fak-strip" role="group" aria-label="Scener"></div>
          <div class="fak-cbar">
            <button class="fak-cb" data-act="prev" aria-label="Forrige scene"><svg viewBox="0 0 16 16"><path d="M3 2h2v12H3zM14 2v12L6 8z"/></svg></button>
            <button class="fak-cb play" data-act="play"><svg viewBox="0 0 16 16" class="fak-pico"><path d="M4 2l10 6-10 6z"/></svg><span class="fak-plabel">Afspil</span></button>
            <button class="fak-cb" data-act="next" aria-label="Næste scene"><svg viewBox="0 0 16 16"><path d="M11 2h2v12h-2zM2 2v12l8-6z"/></svg></button>
            <label class="fak-eyebrow" style="margin-left:6px">Tempo <select class="fak-speed"><option value="0.75">0,75×</option><option value="1" selected>1×</option><option value="1.25">1,25×</option><option value="1.5">1,5×</option></select></label>
            <span class="fak-cpos"></span>
          </div>
        </div>
      </section>
      <section class="fak-law">
        <h2>Lovteksten, ordret</h2>
        <p class="src">Fra <a href="${esc(course.source.url)}" target="_blank" rel="noopener">${esc(course.source.label)}</a> på Retsinformation.</p>
        <div class="fak-lawbody"></div>
      </section>
      <p class="fak-foot">Kurset erstatter ikke lovteksten, forældreansvarsbekendtgørelsen eller forældreansvarsvejledningen. Mellemrum = afspil/pause, piletaster = skift scene.</p>
    </div>
    <aside class="fak-aside" aria-label="Indhold"><h3>Indhold</h3><p class="fak-score"></p><div class="fak-toc"></div></aside>
  </div>`;
  const $ = s => root.querySelector(s);
  const stage = $(".fak-stage"), strip = $(".fak-strip"), lawEl = $(".fak-lawbody"), toc = $(".fak-toc");
  setSync(syncState);

  const ro = new ResizeObserver(([e]) => {
    const w = e.contentRect.width;
    root.classList.toggle("fak-narrow", w < 980);
    root.classList.toggle("fak-small", w < 640);
  });
  ro.observe(root);

  /* ---------- render ---------- */
  const dur = sc => { if (sc.quiz || sc.end) return Infinity; const w = ((sc.s || "") + " " + (sc.pr || "")).split(/\s+/).length; return Math.max(12, w * 0.36 + 4); };
  const score = () => { const a = Object.values(state.answers); return { correct: a.filter(x => x.correct).length, answered: a.length, total: allQs.length }; };

  function lawHTML(sc) {
    if (!sc.law) { lawEl.innerHTML = `<div class="fak-lp"><div class="pn">–</div><div class="lt"><p>${sc.quiz ? "Quizzen bygger på lovteksten fra de foregående scener." : "Denne scene har ingen lovtekst."}</p></div></div>`; return; }
    lawEl.innerHTML = sc.law.map(([p, txt]) => `<div class="fak-lp"><div class="pn">${esc(p)}</div><div class="lt">${txt.split("|").map(x => { const m = x.match(/^(Stk\. \d+\.)(.*)$/); return `<p>${m ? `<span class="sk">${m[1]}</span>${esc(m[2])}` : esc(x)}</p>`; }).join("")}</div></div>`).join("");
  }
  function sceneHTML(sc) {
    const top = `<div class="fak-sctop"><span class="fak-eyebrow">${esc(CH[sc.c])}</span>${sc.p ? `<span class="fak-pchip">${esc(sc.p)}</span>` : ""}${sc.nyt ? `<span class="fak-nyt">${esc(sc.nyt)}</span>` : ""}</div>`;
    if (sc.end) {
      const sc2 = score(), done = !!state.completedAt;
      return top + `<h3 class="fak-title fak-a">${esc(sc.t)}</h3><div class="fak-body"><div><p class="fak-say fak-a" style="--d:.2s">${done ? "Kurset er gennemført og registreret." : "Du mangler stadig nogle scener eller quizspørgsmål. Se indholdsfortegnelsen."} Du har svaret rigtigt på <b>${sc2.correct} af ${sc2.total}</b> quizspørgsmål.</p><p class="fak-prax fak-a" style="--d:.4s"><b>Næste skridt</b>Læs forældreansvarsbekendtgørelsen og vejledningen, og tag en rigtig sag igennem med en erfaren kollega.</p></div><div class="fak-a" style="--d:.5s"><div class="fak-end">Du er ikke alene</div><p class="fak-prax">SkilsmisseVejen · familieretlig rådgivning med barnet i centrum</p></div></div>`;
    }
    if (sc.quiz) return top + `<h3 class="fak-title">${esc(sc.t)}</h3><div class="fak-quiz"></div>`;
    return top + `<h3 class="fak-title fak-a">${esc(sc.t)}</h3><div class="fak-body"><div><p class="fak-say fak-a" style="--d:.1s">${esc(sc.s)}</p>${sc.pr ? `<p class="fak-prax fak-a" style="--d:.35s"><b>I praksis</b>${esc(sc.pr)}</p>` : ""}</div><div class="fak-vis">${vis(sc.v)}</div></div>`;
  }

  function runQuiz(sc) {
    const box = $(".fak-quiz"); let qi = sc.qs.findIndex(q => !(q.id in state.answers));
    if (qi === -1) {
      const ok = sc.qs.filter(q => state.answers[q.id]?.correct).length;
      box.innerHTML = `<p class="fak-say">Du har taget denne quiz: <b>${ok} af ${sc.qs.length}</b> rigtige.</p><div class="fak-row"><button class="fak-qbtn" data-q="next">Fortsæt</button><button class="fak-cb" data-q="retake">Tag den igen</button></div>`;
      box.querySelector('[data-q="next"]').onclick = () => go(idx + 1, true);
      box.querySelector('[data-q="retake"]').onclick = () => { sc.qs.forEach(q => delete state.answers[q.id]); emit("quiz_reset", { sceneId: sc.id }); renderAside(); runQuiz(sc); };
      return;
    }
    const show = () => {
      const q = sc.qs[qi];
      box.innerHTML = `<div class="fak-qcount">Spørgsmål ${qi + 1} af ${sc.qs.length}</div><p class="fak-qq">${esc(q.q)}</p><div class="fak-opts">${q.o.map((o, i) => `<button class="fak-opt" data-i="${i}">${esc(o)}</button>`).join("")}</div><div class="fak-fb"></div>`;
      box.querySelectorAll(".fak-opt").forEach(b => b.onclick = () => {
        const i = +b.dataset.i, ok = i === q.a;
        const prev = state.answers[q.id];
        const attempt = (state.attemptCount?.[q.id] || 0) + 1;
        state.attemptCount = { ...(state.attemptCount || {}), [q.id]: attempt };
        state.answers[q.id] = { choice: i, correct: ok, attempt };
        emit("quiz_answered", { sceneId: sc.id, chapter: sc.c, questionId: q.id, choice: i, correct: ok, attempt });
        box.querySelectorAll(".fak-opt").forEach(x => { x.disabled = true; const j = +x.dataset.i; if (j === q.a) x.classList.add("ok"); else if (j === i) x.classList.add("no"); });
        const lastQ = qi === sc.qs.length - 1;
        const fb = box.querySelector(".fak-fb");
        fb.innerHTML = `<b class="${ok ? "ok" : "no"}">${ok ? "Rigtigt." : "Ikke helt."}</b> ${esc(q.e)}<div class="fak-row"><button class="fak-qbtn">${lastQ ? "Fortsæt kurset" : "Næste spørgsmål"}</button></div>`;
        const btn = fb.querySelector(".fak-qbtn");
        btn.onclick = () => { if (lastQ) go(idx + 1, true); else { qi++; show(); } };
        btn.focus();
        renderAside(); checkComplete();
      });
    };
    show();
  }

  function checkComplete() {
    if (state.completedAt) return;
    const allSeen = contentIds.every(id => state.seen.has(id));
    const allAnswered = allQs.every(q => q.id in state.answers);
    if (allSeen && allAnswered) {
      state.completedAt = new Date().toISOString();
      const s = score();
      emit("course_completed", { correct: s.correct, answered: s.answered, total: s.total });
      try { opts.onComplete?.(s); } catch (e) {}
    }
  }

  function buildStrip() {
    strip.innerHTML = S.map((sc, i) => `<button class="fak-seg${i > 0 && S[i - 1].c !== sc.c ? " gap" : ""}${sc.quiz ? " q" : ""}" data-i="${i}" aria-label="${esc((sc.p ? sc.p + ": " : "") + sc.t)}" title="${esc((sc.p ? sc.p + " · " : "") + sc.t)}"><span class="f"></span></button>`).join("");
    strip.querySelectorAll(".fak-seg").forEach(b => b.onclick = () => go(+b.dataset.i, playing));
  }
  function renderAside() {
    const s = score();
    $(".fak-score").innerHTML = `Quiz: <b>${s.correct}</b> rigtige af <b>${s.answered}</b> besvarede · ${s.total} i alt`;
    let h = "";
    CH.forEach((c, ci) => {
      const ids = S.map((x, i) => [x, i]).filter(([x]) => x.c === ci); if (!ids.length) return;
      const nseen = ids.filter(([x]) => state.seen.has(x.id)).length;
      h += `<div class="fak-chap${S[idx].c === ci ? " cur" : ""}"><button data-i="${ids[0][1]}"><span>${esc(c)}</span><span class="ct">${nseen}/${ids.length}</span></button><div class="fak-plist">${ids.map(([x, i]) => `<button class="fak-pb${state.seen.has(x.id) ? " seen" : ""}${i === idx ? " cur" : ""}${x.quiz ? " qz" : ""}" data-i="${i}">${esc(x.quiz ? "Quiz" : x.p)}</button>`).join("")}</div></div>`;
    });
    toc.innerHTML = h;
    toc.querySelectorAll("button[data-i]").forEach(b => b.onclick = () => { go(+b.dataset.i, playing); root.querySelector(".fak-player").scrollIntoView({ behavior: "smooth", block: "start" }); });
  }
  function paintStrip() {
    const d = dur(S[idx]);
    strip.querySelectorAll(".fak-seg").forEach((b, i) => {
      b.classList.toggle("done", i < idx);
      b.firstChild.style.width = i === idx ? (isFinite(d) ? Math.min(100, t / d * 100) : 100) + "%" : "0";
    });
    $(".fak-cpos").textContent = `Scene ${idx + 1} af ${S.length}`;
  }
  function setPlay(p) {
    playing = p;
    $(".fak-plabel").textContent = p ? "Pause" : "Afspil";
    $(".fak-pico").innerHTML = p ? '<path d="M3 2h4v12H3zM9 2h4v12H9z"/>' : '<path d="M4 2l10 6-10 6z"/>';
    last = performance.now();
  }
  function go(i, keep) {
    if (i < 0 || i >= S.length) return;
    idx = i; t = 0;
    const sc = S[i];
    if (!state.seen.has(sc.id)) { state.seen.add(sc.id); emit("scene_viewed", { sceneId: sc.id, chapter: sc.c }); }
    else emit("scene_opened", { sceneId: sc.id, chapter: sc.c });
    state.lastSceneId = sc.id;
    stage.innerHTML = sceneHTML(sc);
    lawHTML(sc);
    if (sc.quiz) { runQuiz(sc); setPlay(false); }
    else if (sc.end) setPlay(false);
    else setPlay(!!keep);
    renderAside(); paintStrip(); checkComplete();
  }
  function loop(now) {
    if (destroyed) return;
    const dt = (now - last) / 1000; last = now;
    if (playing) { const d = dur(S[idx]); t += dt * speed; if (t >= d) go(idx + 1, true); else paintStrip(); }
    raf = requestAnimationFrame(loop);
  }

  /* ---------- controls ---------- */
  root.querySelector('[data-act="play"]').onclick = () => { if (S[idx].quiz || S[idx].end) { go(Math.min(idx + 1, S.length - 1), true); return; } setPlay(!playing); };
  root.querySelector('[data-act="prev"]').onclick = () => go(idx - 1, playing);
  root.querySelector('[data-act="next"]').onclick = () => go(idx + 1, playing);
  root.querySelector(".fak-speed").onchange = e => speed = +e.target.value;
  const onKey = e => {
    if (e.target.closest("select,input,textarea")) return;
    if (e.key === " " && e.target.closest("button")) return;
    if (e.key === " ") { e.preventDefault(); root.querySelector('[data-act="play"]').click(); }
    else if (e.key === "ArrowRight") go(idx + 1, playing);
    else if (e.key === "ArrowLeft") go(idx - 1, playing);
  };
  root.addEventListener("keydown", onKey);

  /* ---------- start ---------- */
  async function start() {
    stage.innerHTML = `<p class="fak-say">Henter din fremgang…</p>`;
    try {
      let p = opts.initialState;
      if (!p && opts.loadProgress) p = await opts.loadProgress();
      else if (!p && apiBase && !opts.onEvent) {
        const r = await fetch(apiBase + "/progress", { credentials: "include", ...fetchInit });
        if (r.ok) p = await r.json();
      }
      applyProgress(p);
    } catch (e) { setSync("err"); }
    if (destroyed) return;
    buildStrip();
    const startIdx = state.lastSceneId && idxById.has(state.lastSceneId) ? idxById.get(state.lastSceneId) : 0;
    if (state.seen.size === 0) emit("course_started", {});
    go(startIdx, false);
    raf = requestAnimationFrame(t0 => { last = t0; raf = requestAnimationFrame(loop); });
    flush();
  }
  start();

  return {
    destroy() { destroyed = true; cancelAnimationFrame(raf); ro.disconnect(); root.removeEventListener("keydown", onKey); root.innerHTML = ""; },
    getState() { return { seen: [...state.seen], answers: { ...state.answers }, lastSceneId: state.lastSceneId, completedAt: state.completedAt }; },
    goTo(sceneId) { if (idxById.has(sceneId)) go(idxById.get(sceneId), false); }
  };
}

export default mountCourse;
